import { useMemo, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import {
  activeChild,
  childQuotes,
  childWork,
  rubricDomains,
  rubricHistory,
  rubricMoves,
  type RubricDomain,
} from '../../store/selectors'
import { formatFriendlyDate, todayISO } from '../../lib/dates'

/**
 * The keepsake.
 *
 * Everything on these pages is real: her words are verbatim, her drawings are
 * the actual files, and the things she can do are the ones with evidence behind
 * them. Nothing here is written for her — a book that put sentences in a
 * child's mouth would be worth nothing in ten years, and that is the only
 * timescale this page is designed for.
 *
 * It prints. A screen is not a thing you keep.
 */
export function LookWhatICanDo({ onClose }: { onClose?: () => void }) {
  const child = useAuroraStore(activeChild)
  const domains = useAuroraStore(rubricDomains) as RubricDomain[]
  const history = useAuroraStore(rubricHistory) as any[]
  const quotes = useAuroraStore(childQuotes)
  const work = useAuroraStore(childWork) as any[]
  const [saved, setSaved] = useState(false)

  const name = (child as any)?.name ?? 'Me'
  const today = todayISO((child as any)?.timezone)

  /** Things she can do: what she is strong at, and what climbed this cycle. */
  const canDo = useMemo(() => {
    const strong = domains.flatMap((d) =>
      d.skills
        .filter((sk) => (sk.level ?? 0) >= 4)
        .map((sk) => ({ name: sk.name, domain: d.name, note: sk.note, moved: 0 })),
    )

    if (history.length > 1) {
      const moved = rubricMoves(history[0]?.data, history[history.length - 1]?.data)
        .filter((m) => (m.delta ?? 0) > 0)
        .map((m) => ({ name: m.name, domain: m.domain, note: m.note, moved: m.delta ?? 0 }))

      const byName = new Map(strong.map((s) => [s.name, s]))
      for (const m of moved) byName.set(m.name, { ...(byName.get(m.name) ?? m), moved: m.moved })
      return [...byName.values()].sort((a, b) => b.moved - a.moved).slice(0, 12)
    }

    return strong.slice(0, 12)
  }, [domains, history])

  const pictures = work.filter((a) => a.storage?.url).slice(0, 6)
  const words = quotes.slice(0, 8)
  const empty = canDo.length === 0 && pictures.length === 0 && words.length === 0

  const save = () => {
    ;(auroraStore.getState() as any).createArtifact({
      childId: (child as any)?._id,
      kind: 'book',
      title: `Look what I can do, by ${name}`,
      summary: `${canDo.length} things she can do, ${words.length} of her own words, ${pictures.length} pictures.`,
      source: 'parent',
      tags: ['book', 'keepsake'],
      data: {
        madeOn: today,
        canDo,
        quotes: words.map((q) => ({ quote: q.quote, at: q.at })),
        artifactIds: pictures.map((p) => p._id),
      },
    })
    setSaved(true)
  }

  if (empty) {
    return (
      <div className="book-empty">
        <p className="empty-title">The book writes itself, once there is something in it</p>
        <p className="empty-body">
          Capture a few moments, save some of {name}’s drawings, and this fills up on its own. Her
          words go in exactly as she said them.
        </p>
        {onClose && (
          <div className="empty-action">
            <button className="btn btn-ghost" onClick={onClose}>
              Not yet
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="book-wrap">
      <article className="book">
        <header className="book-cover">
          <div className="book-cover-sky" aria-hidden="true" />
          <p className="book-cover-eyebrow">Look what I can do</p>
          <h1 className="book-cover-name">{name}</h1>
          <p className="book-cover-date">{formatFriendlyDate(today, (child as any)?.timezone)}</p>
        </header>

        {canDo.length > 0 && (
          <section className="book-page">
            <h2 className="book-page-title">Things I can do</h2>
            <ul className="book-can">
              {canDo.map((c) => (
                <li key={c.name}>
                  <span className="book-can-name">{c.name}</span>
                  {c.note && <span className="book-can-note">{c.note}</span>}
                  {c.moved > 0 && (
                    <span className="book-can-moved">
                      {c.moved === 1 ? 'new this cycle' : 'a big jump this cycle'}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {words.length > 0 && (
          <section className="book-page">
            <h2 className="book-page-title">Things I said</h2>
            <ul className="book-quotes">
              {words.map((q, i) => (
                <li key={`${q.captureId}:${i}`}>
                  <blockquote>“{q.quote}”</blockquote>
                </li>
              ))}
            </ul>
          </section>
        )}

        {pictures.length > 0 && (
          <section className="book-page">
            <h2 className="book-page-title">Things I made</h2>
            <div className="book-gallery">
              {pictures.map((p) => (
                <figure key={p._id}>
                  <img src={p.storage.url} alt={p.title} />
                  <figcaption>{p.title}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        <footer className="book-end">
          <p>Made with Sapling · {formatFriendlyDate(today, (child as any)?.timezone)}</p>
        </footer>
      </article>

      <div className="book-actions">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <PrintIcon /> Print it
        </button>
        <button className="btn btn-ghost" onClick={save} disabled={saved}>
          {saved ? 'Kept in the library' : 'Keep it'}
        </button>
      </div>
    </div>
  )
}

const PrintIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.9}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9V3h12v6" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
    <path d="M6 14h12v7H6z" />
  </svg>
)
